// @ts-nocheck
import {
  Box,
  Card as MantineCard,
  SimpleGrid,
  Stack,
  Text as MantineText,
  ThemeIcon,
  Title,
  Badge,
  Group,
} from '@mantine/core';
import {
  IconComponents,
  IconSparkles,
  IconLayout,
  IconBriefcase,
  IconSettings,
  IconPalette,
  IconClick,
  IconChartBar,
  IconDatabase,
  IconUser,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Card, Text } from '../../components/ui';

const categories = [
  {
    icon: IconComponents,
    title: 'UI Components',
    description: 'Core building blocks, data display, feedback, inputs, navigation, overlays, and layout components',
    path: '/app/demo/ui',
    color: 'blue',
    count: '60+ components',
  },
  {
    icon: IconSparkles,
    title: 'Advanced Components',
    description: 'Specialized components for entities, calendar, workflows, documents, reporting, and more',
    path: '/app/demo/advanced',
    color: 'violet',
    count: '40+ components',
  },
  {
    icon: IconClick,
    title: 'Animations',
    description: 'Transitions, attention effects, scroll animations, loading states, gestures, and page transitions',
    path: '/app/demo/animations',
    color: 'pink',
    count: '30+ animations',
  },
  {
    icon: IconBriefcase,
    title: 'Domain Examples',
    description: 'Real-world examples for Projects, Tasks, Members, Procurement, Meetings, Reports, Forms, Files, HR, Accounting, CRM, and Users',
    path: '/app/demo/domains',
    color: 'green',
    count: '12 domains',
  },
  {
    icon: IconSettings,
    title: 'Settings & Configuration',
    description: 'Color schemes, theme modes, typography, translations, and system preferences',
    path: '/app/demo/settings',
    color: 'orange',
    count: '8 pages',
  },
];

const highlights = [
  {
    icon: IconPalette,
    title: '30 Color Schemes',
    description: 'Beautiful gradients and palettes',
    color: 'blue',
  },
  {
    icon: IconDatabase,
    title: 'Rich Mock Data',
    description: 'Relational data with 30+ users, 20+ projects, 30+ tasks',
    color: 'green',
  },
  {
    icon: IconChartBar,
    title: 'Complete Examples',
    description: 'End-to-end implementations for each domain',
    color: 'orange',
  },
  {
    icon: IconUser,
    title: 'Production Ready',
    description: 'TypeScript, accessibility, responsive design',
    color: 'violet',
  },
];

export default function DemoOverviewPage() {
  const navigate = useNavigate();

  return (
    <Stack gap="xl">
      {/* Header */}
      <Box>
        <Text gradient gradientVariant="primary" size="xl" fw={700}>
          Component Showcase
        </Text>
        <MantineText c="dimmed" size="sm">
          Explore our comprehensive library of UI components, animations, and domain examples with rich mock data
        </MantineText>
      </Box>

      {/* Welcome Card */}
      <Card variant="gradient" gradientVariant="primary" hoverEffect="glow">
        <Stack gap="md">
          <Group gap="lg">
            <ThemeIcon size={60} radius="xl" variant="white" color="dark">
              <IconComponents size={32} />
            </ThemeIcon>
            <Box style={{ flex: 1 }}>
              <Title order={3} c="white">
                Welcome to the Component Library
              </Title>
              <MantineText c="white" opacity={0.9}>
                A comprehensive showcase featuring 168+ components, 30+ animations, and 12 complete domain examples.
                All components are theme-aware, responsive, and production-ready.
              </MantineText>
            </Box>
          </Group>
        </Stack>
      </Card>

      {/* Main Categories */}
      <Box>
        <Title order={3} mb="md">
          Browse by Category
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {categories.map((category) => (
            <MantineCard
              key={category.path}
              shadow="sm"
              padding="lg"
              radius="md"
              withBorder
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onClick={() => navigate(category.path)}
            >
              <Stack gap="md">
                <Group>
                  <ThemeIcon size={48} radius="md" variant="light" color={category.color}>
                    <category.icon size={24} />
                  </ThemeIcon>
                  <Badge size="sm" variant="light" color={category.color}>
                    {category.count}
                  </Badge>
                </Group>
                <Box>
                  <MantineText fw={600} size="lg">
                    {category.title}
                  </MantineText>
                  <MantineText size="sm" c="dimmed" mt={4}>
                    {category.description}
                  </MantineText>
                </Box>
              </Stack>
            </MantineCard>
          ))}
        </SimpleGrid>
      </Box>

      {/* Highlights */}
      <Box>
        <Title order={3} mb="md">
          Highlights
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          {highlights.map((highlight) => (
            <MantineCard
              key={highlight.title}
              shadow="sm"
              padding="md"
              radius="md"
              withBorder
            >
              <Stack gap="sm" align="center" ta="center">
                <ThemeIcon size={40} radius="md" variant="light" color={highlight.color}>
                  <highlight.icon size={20} />
                </ThemeIcon>
                <Box>
                  <MantineText fw={600} size="sm">
                    {highlight.title}
                  </MantineText>
                  <MantineText size="xs" c="dimmed" mt={4}>
                    {highlight.description}
                  </MantineText>
                </Box>
              </Stack>
            </MantineCard>
          ))}
        </SimpleGrid>
      </Box>

      {/* Quick Links */}
      <Box>
        <Title order={4} mb="md">
          Popular Components
        </Title>
        <Group gap="xs">
          <Badge
            size="lg"
            variant="light"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/demo/ui/data-display')}
          >
            DataTable
          </Badge>
          <Badge
            size="lg"
            variant="light"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/demo/ui/inputs')}
          >
            Forms
          </Badge>
          <Badge
            size="lg"
            variant="light"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/demo/advanced/calendar')}
          >
            Calendar
          </Badge>
          <Badge
            size="lg"
            variant="light"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/demo/advanced/kanban')}
          >
            Kanban Board
          </Badge>
          <Badge
            size="lg"
            variant="light"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/demo/animations/transitions')}
          >
            Animations
          </Badge>
          <Badge
            size="lg"
            variant="light"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/app/demo/domains/projects')}
          >
            Project Examples
          </Badge>
        </Group>
      </Box>

      {/* Getting Started */}
      <MantineCard shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="sm">
          <Title order={4}>Getting Started</Title>
          <MantineText size="sm" c="dimmed">
            This demo showcases all available components with realistic mock data. Each component page includes:
          </MantineText>
          <Box component="ul" style={{ marginTop: 8, paddingLeft: 20 }}>
            <li>
              <MantineText size="sm">Live interactive examples</MantineText>
            </li>
            <li>
              <MantineText size="sm">Inline code snippets showing JSX usage</MantineText>
            </li>
            <li>
              <MantineText size="sm">Different variants and configurations</MantineText>
            </li>
            <li>
              <MantineText size="sm">Props documentation and descriptions</MantineText>
            </li>
          </Box>
          <MantineText size="sm" c="dimmed" mt="xs">
            Use the navigation sidebar to browse by category, or click any card above to jump to a specific section.
          </MantineText>
        </Stack>
      </MantineCard>
    </Stack>
  );
}
